import { FormContainer } from '@strata/react-form-container';
import { TextInputFormik } from '@strata/react-inputs';
import { Spinner } from '@strata/react-loading-animation';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { SuccessToast } from '@strata/react-toast-message';
import { Worklist } from '@strata/react-worklist';
import { validationStrings } from '@strata/strata-ui-strings';
import { Formik } from 'formik';
import { useState } from 'react';
import * as Yup from 'yup';

import { postRebillingTask } from 'api/oopsAPI';
import { handleError } from 'services/Error';
import { useLazyGetActiveReferralsQuery } from 'store/api/oopsApi';
import { useGetSupplierListQuery } from 'store/api/referenceApi';

const ValidationSchema = Yup.object({
    supplier_npi: Yup.string().required(validationStrings.requiredField.errorMessage),
    referral_id: Yup.string().required(validationStrings.requiredField.errorMessage),
    payer_id: Yup.string().required(validationStrings.requiredField.errorMessage),
    claim_id: Yup.string().required(validationStrings.requiredField.errorMessage),
});

export const RebillingTask = () => {
    const [taskLog, setTaskLog] = useState<object>();
    const { data: supplierList, isFetching: supplierListFetching } = useGetSupplierListQuery();

    const [getActiveReferrals, { data: referralList, isFetching: referralListFetching }] =
        useLazyGetActiveReferralsQuery();

    return (
        <Formik
            initialValues={{} as Yup.InferType<typeof ValidationSchema>}
            validationSchema={ValidationSchema}
            onSubmit={async (values) => {
                try {
                    const response = await postRebillingTask({
                        payer_id: values.payer_id,
                        referral_id: values.referral_id,
                        supplier_npi: values.supplier_npi,
                        claim_id: values.claim_id,
                        task_type: Worklist.WorkTaskType.REBILL,
                    });
                    SuccessToast({ message: 'Rebilling task created.' });
                    setTaskLog(response);
                } catch (err) {
                    handleError('Error creating rebilling task.', err as Error);
                }
            }}
        >
            {(formik) => (
                <FormContainer
                    formTitle="Create Rebilling Task"
                    primaryButtonLabel="Create Rebilling Task"
                    onFormSubmit={formik.handleSubmit}
                    isSubmitting={formik.isSubmitting || !(formik.dirty && formik.isValid)}
                    onCancelClick={formik.resetForm}
                    formLeftContent={
                        <>
                            <SuggestSingleSelectFormik
                                id="supplier_npi"
                                label="Supplier NPI"
                                required
                                isLoading={supplierListFetching}
                                values={supplierList}
                                selectedItem={supplierList?.find(
                                    ({ npi }) => npi === formik.values.supplier_npi,
                                )}
                                onChange={(newValue) => {
                                    void (async () => {
                                        await formik.setFieldValue('supplier_npi', newValue?.npi);
                                        if (newValue?.npi) {
                                            await getActiveReferrals({ supplierNpi: newValue.npi });
                                        }
                                    })();
                                }}
                            />
                            <SuggestSingleSelectFormik
                                id="referral_id"
                                label="Supplier Referral"
                                required
                                isLoading={referralListFetching}
                                values={referralList}
                                selectedItem={referralList?.find(
                                    ({ referralId }) => referralId === formik.values.referral_id,
                                )}
                                onChange={(newValue) => {
                                    void (async () => {
                                        await formik.setFieldValue(
                                            'referral_id',
                                            newValue?.referralId,
                                        );
                                        await formik.setFieldValue('payer_id', newValue?.payerId);
                                    })();
                                }}
                            />
                            <TextInputFormik label="Claim ID" id="claim_id" required />
                        </>
                    }
                    formRightContent={
                        <>
                            <div>Rebilling Task Log:</div>
                            {formik.isSubmitting ? (
                                <Spinner intent={Spinner.Intent.NONE} />
                            ) : (
                                <pre>{taskLog && JSON.stringify(taskLog, null, 2)}</pre>
                            )}
                        </>
                    }
                />
            )}
        </Formik>
    );
};
